import React, { useState } from 'react';

/**
 * Client confirmation badge for the claim header (Barclays sole-representative
 * email, Dec 4-5 Nov 2026).
 *
 * The Barclays email's "click here to confirm" link is either our Confirm page
 * (claim.clientConfirmations) or, from 5 Nov, a re-authority LOA portal link:
 * the client signs a fresh LOA and nothing is sent (no LOC, no confirmation
 * email). The email's correspondence entry carries metadata.event
 * 'confirmation-email-sent', the campaign and, for an LOA link, loaRequestId.
 *
 * Green  "✓ Representation confirmed"      confirmed on the Confirm page, or the
 *                                           LOA from that link is signed
 *                                           (claim.loaSubmissions[].loaRequestId).
 *        "✓ Representation confirmed · ID check pending"
 *                                           signed through the link but held for a
 *                                           staff ID review: the client HAS
 *                                           confirmed (Dec, 5 Nov 2026); staff still
 *                                           approve the ID check before the new LOA
 *                                           is saved. Every claim on the link shows
 *                                           it (loaIdentityHold.requestId).
 *                                           A REJECTED check goes back to amber.
 * Amber  "Representation confirmation requested"
 *                                           the email went, nothing back yet.
 *
 * Click a badge for the detail. Nothing here changes the claim.
 */

export interface ClientConfirmationStamp {
  label?: string;
  confirmedAt?: string;
  tokenId?: string;
  ip?: string;
  userAgent?: string;
  statement?: string;
  emailedTo?: string;
  how?: string;
}

export type ConfirmationState = 'confirmed' | 'awaiting';

export interface ConfirmationBadgeInfo {
  campaign: string;
  label: string;
  state: ConfirmationState;
  confirmed?: ClientConfirmationStamp;
  sentAt?: string;
  sentTo?: string;
  /** Signed through the link, but the staff ID check is still to do. */
  idCheckPending?: boolean;
  /** The claim whose LOA Identity Review panel holds the check. */
  reviewClaimId?: string;
  /** Signed, but staff rejected the ID check: nothing was saved. */
  idCheckRejected?: boolean;
}

const CAMPAIGN_LABELS: Record<string, string> = {
  'barclays-sole-rep-2026-11': 'Barclays',
};

const LOA_HOW = 'Signed a new Letter of Authority (re-authority link)';

const labelFor = (campaign: string, stamp?: ClientConfirmationStamp): string =>
  stamp?.label || CAMPAIGN_LABELS[campaign] || campaign;

/** "04/11/2026 4:07pm", UK time. */
export const formatUkDateTime = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = date.toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
  const time = date
    .toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\s/g, '')
    .toLowerCase();
  return `${day} ${time}`;
};

const asRecord = (value: unknown): Record<string, any> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : null;

const asList = (value: unknown): Record<string, any>[] => {
  if (Array.isArray(value)) return value.map(asRecord).filter((v): v is Record<string, any> => !!v);
  const record = asRecord(value);
  return record ? Object.values(record).map(asRecord).filter((v): v is Record<string, any> => !!v) : [];
};

interface Send { sentAt: string; sentTo: string; loaRequestId?: string }

const when = (iso?: string): number => {
  const t = new Date(iso || '').getTime();
  return Number.isNaN(t) ? 0 : t;
};

/** One badge per campaign: confirmed beats awaiting; of two confirmations the LATEST shows. */
export function confirmationBadges(
  clientConfirmations: unknown,
  correspondence: readonly unknown[] | null | undefined,
  loaSubmissions?: unknown,
  loaIdentityReviews?: unknown,
  loaIdentityHold?: unknown,
): ConfirmationBadgeInfo[] {
  const badges = new Map<string, ConfirmationBadgeInfo>();

  // 1. Confirmed on our Confirm page (kept aside: a later LOA signing can supersede it).
  const pageConfirmed = new Map<string, ConfirmationBadgeInfo>();
  Object.entries(asRecord(clientConfirmations) || {}).forEach(([campaign, raw]) => {
    const stamp = asRecord(raw) as ClientConfirmationStamp | null;
    if (!stamp?.confirmedAt) return;
    pageConfirmed.set(campaign, { campaign, label: labelFor(campaign, stamp), state: 'confirmed', confirmed: stamp });
  });

  // 2. Every send of the email, per campaign.
  const sends = new Map<string, Send[]>();
  (correspondence || []).forEach((raw) => {
    const entry = asRecord(raw);
    const meta = asRecord(entry?.metadata);
    if (!entry || !meta || meta.event !== 'confirmation-email-sent' || !meta.campaign) return;
    const campaign = String(meta.campaign);
    const to = Array.isArray(entry.to) ? entry.to.join(', ') : String(entry.to || '');
    const list = sends.get(campaign) || [];
    list.push({
      sentAt: String(entry.timestamp || ''),
      sentTo: to,
      loaRequestId: typeof meta.loaRequestId === 'string' ? meta.loaRequestId : undefined,
    });
    sends.set(campaign, list);
  });

  const submissions = asList(loaSubmissions);
  const reviews = asList(loaIdentityReviews);
  const hold = asRecord(loaIdentityHold);

  sends.forEach((list, campaign) => {
    const page = pageConfirmed.get(campaign);
    const latest = [...list].sort((a, b) => (a.sentAt < b.sentAt ? 1 : -1))[0];
    const label = labelFor(campaign);

    // 3. An LOA signed through one of this campaign's links.
    const requestIds = new Set(list.map((s) => s.loaRequestId).filter((id): id is string => !!id));
    const signed = submissions
      .filter((s) => requestIds.has(String(s.loaRequestId)))
      .sort((a, b) => (String(a.submittedAt) < String(b.submittedAt) ? 1 : -1))[0];
    if (signed) {
      const send = list.find((s) => s.loaRequestId === signed.loaRequestId) || latest;
      const audit = asRecord(signed.audit) || {};
      if (page && when(page.confirmed?.confirmedAt) > when(String(signed.submittedAt || ''))) {
        badges.set(campaign, page);   // the Confirm page was the later of the two
        return;
      }
      badges.set(campaign, {
        campaign, label, state: 'confirmed', sentAt: send.sentAt, sentTo: send.sentTo,
        confirmed: {
          label, confirmedAt: String(signed.submittedAt || ''), how: LOA_HOW,
          ip: audit.ipAddress || undefined, userAgent: audit.userAgent || undefined, emailedTo: send.sentTo,
        },
      });
      return;
    }

    // 4. Signed through the link but held for a staff ID check. The client HAS
    //    confirmed by signing (Dec, 5 Nov 2026), so it shows as confirmed with an
    //    "ID check pending" reminder. The review lives on the link's main claim;
    //    every claim on the link carries loaIdentityHold with its requestId.
    const review = reviews.find((r) => requestIds.has(String(r.requestId)));
    const holdHere = !!hold && requestIds.has(String(hold.requestId));
    const rejected = review?.state === 'rejected';
    if (!rejected && ((review && review.state !== 'completed') || (!review && holdHere))) {
      const reqId = String(review?.requestId || hold?.requestId || '');
      const send = list.find((s) => s.loaRequestId === reqId) || latest;
      const signedAt = String(review?.createdAt || '');
      if (page && when(page.confirmed?.confirmedAt) > when(signedAt)) {
        badges.set(campaign, page);
        return;
      }
      badges.set(campaign, {
        campaign, label, state: 'confirmed', idCheckPending: true, sentAt: send.sentAt, sentTo: send.sentTo,
        reviewClaimId: String(review?.primaryClaimId || hold?.primaryClaimId || ''),
        confirmed: { label, confirmedAt: signedAt || undefined, how: LOA_HOW, emailedTo: send.sentTo },
      });
      return;
    }

    if (page) {
      badges.set(campaign, page);
      return;
    }

    badges.set(campaign, {
      campaign, label, state: 'awaiting', sentAt: latest.sentAt, sentTo: latest.sentTo,
      ...(rejected ? { idCheckRejected: true } : {}),
    });
  });

  pageConfirmed.forEach((info, campaign) => {
    if (!badges.has(campaign)) badges.set(campaign, info);   // confirmed with no email entry logged
  });
  return Array.from(badges.values());
}

const COLOURS: Record<ConfirmationState, [string, string, string]> = {
  confirmed: ['#dcfce7', '#166534', '#86efac'],
  awaiting: ['#fef3c7', '#92400e', '#fcd34d'],
};

const pill = (state: ConfirmationState): React.CSSProperties => ({
  backgroundColor: COLOURS[state][0],
  color: COLOURS[state][1],
  border: `1px solid ${COLOURS[state][2]}`,
  padding: '2px 8px',
  borderRadius: '9999px',
  fontSize: '12px',
  fontWeight: 600,
  marginLeft: '4px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  lineHeight: 1.6,
});

const panel: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  zIndex: 50,
  width: '380px',
  maxWidth: '80vw',
  background: '#ffffff',
  color: '#111827',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
  padding: '12px 14px',
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: 1.5,
  whiteSpace: 'normal',
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px', padding: '3px 0' }}>
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span style={{ wordBreak: 'break-word' }}>{children}</span>
  </div>
);

// Dec 4 Nov: "it should say Representation confirmed" (not "Barclays
// confirmed", which reads as if the lender confirmed). The lender is named in
// the pop-up and the tooltip.
const TEXT: Record<ConfirmationState, string> = {
  confirmed: '✓ Representation confirmed',
  awaiting: 'Representation confirmation requested',
};
const ID_CHECK_SUFFIX = ' · ID check pending';

const Badge: React.FC<{ info: ConfirmationBadgeInfo }> = ({ info }) => {
  const [open, setOpen] = useState(false);
  const stamp = info.confirmed;
  const text = TEXT[info.state] + (info.idCheckPending ? ID_CHECK_SUFFIX : '');
  const reviewWhere = info.reviewClaimId ? ` on ${info.reviewClaimId}` : '';
  const tooltip = info.idCheckPending
    ? `${info.label}: the client confirmed by signing; approve the ID check in the LOA Identity Review panel${reviewWhere}`
    : {
      confirmed: `${info.label}: client confirmed Complex Law as sole representative on ${formatUkDateTime(stamp?.confirmedAt)}`,
      awaiting: `${info.label}: confirmation email sent ${formatUkDateTime(info.sentAt)}; the client hasn't confirmed yet`,
    }[info.state];
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        style={pill(info.state)}
        title={tooltip}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {text}
      </button>
      {open && (
        <div role="dialog" aria-label={`${info.label} confirmation details`} style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <strong>{`${text.replace(/^✓ /, '')} (${info.label})`}</strong>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px', color: '#6b7280' }}
            >
              ×
            </button>
          </div>
          {info.state === 'confirmed' && (
            <>
              <Row label="Confirmed">
                {stamp?.confirmedAt ? formatUkDateTime(stamp.confirmedAt) : `Signed (time on ${info.reviewClaimId || 'the main claim'})`}
              </Row>
              {stamp?.how ? <Row label="How">{stamp.how}</Row> : <Row label="Statement">“{stamp?.statement}”</Row>}
              <Row label="Link sent to">{stamp?.emailedTo || 'unknown'}</Row>
              {info.idCheckPending ? (
                <Row label="ID check">
                  Pending. Approve or reject it in the LOA Identity Review panel{reviewWhere}. The new LOA is saved once
                  it is approved; nothing is sent either way.
                </Row>
              ) : (
                <>
                  <Row label="IP address">{stamp?.ip || 'unknown'}</Row>
                  <Row label="Device">{stamp?.userAgent || 'unknown'}</Row>
                </>
              )}
            </>
          )}
          {info.state === 'awaiting' && (
            <>
              <Row label="Email sent">{formatUkDateTime(info.sentAt)}</Row>
              <Row label="Sent to">{info.sentTo || 'unknown'}</Row>
              <Row label="Status">
                {info.idCheckRejected
                  ? 'The client signed, but staff rejected the ID check, so nothing was saved. Send a new link if needed.'
                  : 'Not confirmed yet. The link lasts 90 days.'}
              </Row>
            </>
          )}
        </div>
      )}
    </span>
  );
};

interface ClientConfirmationBadgeProps {
  clientConfirmations?: unknown;
  correspondence?: readonly unknown[] | null;
  loaSubmissions?: unknown;
  loaIdentityReviews?: unknown;
  loaIdentityHold?: unknown;
}

const ClientConfirmationBadge: React.FC<ClientConfirmationBadgeProps> = ({
  clientConfirmations, correspondence, loaSubmissions, loaIdentityReviews, loaIdentityHold,
}) => {
  const badges = confirmationBadges(clientConfirmations, correspondence, loaSubmissions, loaIdentityReviews,
    loaIdentityHold);
  if (badges.length === 0) return null;
  return (
    <>
      {badges.map((info) => (
        <Badge key={info.campaign} info={info} />
      ))}
    </>
  );
};

export default ClientConfirmationBadge;
